import { transition, trigger, useAnimation } from '@angular/animations';
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { logImpression, logUserAction } from '@office-start/telemetry-service';
import { Area, KnownImpressionInstrumentationEvents, Referral } from '@office-start/telemetry-types';
import { getReferral } from '@office-start/telemetry-utility';
import { Subscription } from 'rxjs';

import { AppGalleryConfigService } from '../app-gallery/app-gallery-config.service';
import { linearFadeOut } from '../shared/animations/animations';
import { OfficeRoute } from '../shared/constants/office-route.enum';
import { CreateMenuService } from '../shared/create-menu/create-menu.service';
import { InstrumentationParam, InstrumentationParams, InstrumentationService, KnownAreas, KnownInstrumentationEvents } from '../shared/instrumentation/instrumentation.service';
import { PaintMarkerName } from '../shared/models/paint-marker-name.enum';
import { Workload } from '../shared/models/workload.model';
import { BrowserPaintMeasurementService } from '../shared/services/browser-paint-measurement.service';
import { BrowserPaintService } from '../shared/services/browser-paint.service';
import { ConsumerSkuType } from '../shared/services/consumer-license.service';
import { FloodgateService } from '../shared/services/floodgate.service';
import { LocalStorageService } from '../shared/services/local-storage.service';
import { OfficeRouteService } from '../shared/services/office-route.service';
import { PerformanceService } from '../shared/services/performance.service';
import { WorkloadsService } from '../shared/services/workloads.service';
import { CapabilitiesService } from '../shared/shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared/shared-context/client-startup-config.service';
import { ConsumerInstallConfigService } from '../shared/shared-context/consumer-install-config.service';
import { OneDriveUploadService } from '../upload/upload.service';
import { UnpinWorkloadEvent } from './app-tile-state-event.model';
import { AppTilesConfigService } from './shared/app-tiles-config.service';

import './app-tiles-list.component.css';

const enum AppTilesState {
    All = 'All',   // All tiles are displayed.
    Reduced = 'Reduced' // Only Reduced set of tiles are displayed.
}

// NOTE: Only to be used in mobile views
@Component({
    selector: 'ohp-app-tiles-list',
    templateUrl: './app-tiles-list.component.html',
    animations: [
        trigger('fadeOut', [
            transition(':leave', useAnimation(linearFadeOut))
        ])
    ]
})
export class AppTilesListComponent implements OnInit, AfterViewInit, OnDestroy {

    @ViewChildren('appGalleryLink') public appGalleryLink: QueryList<ElementRef>;

    @Input('fullBleedTheming') public fullBleedTheming: boolean = false;

    @Input('fullBleedFontColor') public fullBleedFontColor: string = '';

    @Input('fullBleedBackplateEnabled') public fullBleedBackplateEnabled: boolean = false;

    @Input('fullBleedBackplateStyle') public fullBleedBackplateStyle: { [key: string]: string } = {};

    public appTilesLandmarkAriaLabel: string;
    public componentArea: string;
    public exploreYourAppsAriaLabel: string;
    public exploreYourAppsText: string;
    public workloads: Array<Readonly<Workload>>;
    public sectionTitle: string;
    public showProvisioningAlert: boolean;
    public provisioningAlertText: string;
    public showLessAppTilesText: string;
    public showAppGalleryLink: boolean;
    public rectangularMobileMaxInitialItems = 4;
    public reducedTilesCount: number;
    public myAccountLinkText: string;
    public isConsumer: boolean;
    public isPaidConsumer: boolean;
    public myAccountUrl: string;
    public bottomTrayEnabled: boolean;
    public createPlaceholderText: string;
    public appGalleryRoute = OfficeRoute.AppGallery;
    public createMenuEnabled = false;

    private static loggedMarker = false;

    private appTilesState: AppTilesState;
    private appGalleryLinkSubscription: Subscription;
    private previousRouteSubscription: Subscription;
    private previousRoute: OfficeRoute;
    private thirdPartyWorkloadsSubscription: Subscription;

    constructor(
        private readonly appGalleryConfigService: AppGalleryConfigService,
        private readonly appTilesConfigService: AppTilesConfigService,
        private readonly capabilitiesService: CapabilitiesService,
        private readonly consumerInstallConfigService: ConsumerInstallConfigService,
        private readonly floodgateService: FloodgateService,
        private readonly instrumentationService: InstrumentationService,
        private readonly performanceService: PerformanceService,
        private readonly officeRouteService: OfficeRouteService,
        private readonly startupConfigService: ClientStartupConfigService,
        private readonly workloadsService: WorkloadsService,
        private readonly browserPaintMeasurementService: BrowserPaintMeasurementService,
        private readonly localStorageService: LocalStorageService,
        private readonly createMenuService: CreateMenuService,
        private readonly oneDriveUploadService: OneDriveUploadService,
        private readonly browserPaintService: BrowserPaintService,
        private readonly changeDetector: ChangeDetectorRef
    ) {
    }

    public ngAfterViewInit() {
        // Log the time passed since user started navigation to office.com until tiles are rendered.
        // Ensure that this is done only once.
        if (!AppTilesListComponent.loggedMarker) {
            this.performanceService.mark(KnownInstrumentationEvents.AppTilesReady);
            AppTilesListComponent.loggedMarker = true;
        }

        this.previousRouteSubscription = this.officeRouteService.subscribeToRouteChangeEnd(
            event => this.previousRoute = event.previousRoute ? event.previousRoute.route : null);

        // Focus on App Gallery link when navigating back to home page.
        this.appGalleryLinkSubscription = this.appGalleryLink.changes.subscribe(links => {
            if (this.previousRoute === OfficeRoute.AppGallery && links && links.length === 1) {
                // Run after paint to avoid synchronous layout computation.
                this.browserPaintService.runAfterPaint(() => {
                    links.first.nativeElement.focus();
                    this.appGalleryLinkSubscription.unsubscribe();
                });
            }
        });

        this.browserPaintMeasurementService.recordWhenPainted(PaintMarkerName.App_Tiles);
    }

    public ngOnInit() {
        this.componentArea = KnownAreas.AppTiles;
        const config = this.appTilesConfigService.config;
        const resources = config.resources;
        this.sectionTitle = resources && resources.AppTilesHeader;
        this.provisioningAlertText = resources && resources.ProvisioningAlertText;
        this.showLessAppTilesText = resources && resources.ShowLessAppTiles;
        this.exploreYourAppsAriaLabel = resources && resources.ExploreYourAppsAriaLabel;
        this.exploreYourAppsText = resources && resources.ExploreYourAppsText;
        this.appTilesLandmarkAriaLabel = resources && resources.AppTilesListLandmarkAriaLabel;
        this.myAccountLinkText = resources && resources.MyAccountConsumerLinkText;
        this.myAccountUrl = this.appTilesConfigService.config.myAccountUrl;
        this.isConsumer = this.startupConfigService.config.authType === AuthType.MSA;
        this.bottomTrayEnabled = this.capabilitiesService.capabilities.bottomTrayEnabled;

        if (this.isConsumer) {
            this.consumerInstallConfigService.getConfig().then(consumerInstallConfig => {
                const subscriptionStatus = consumerInstallConfig && consumerInstallConfig.currentSkuInfo ? consumerInstallConfig.currentSkuInfo.skuType : ConsumerSkuType.NotDetected;
                this.isPaidConsumer = !(subscriptionStatus === ConsumerSkuType.NotSubscribed || subscriptionStatus === ConsumerSkuType.NonActive || subscriptionStatus === ConsumerSkuType.NotDetected);
            });
        }

        this.createMenuService.createButtonEnabledPromise.then(createButtonEnabled => {
            this.createMenuEnabled = this.createMenuEnabled || createButtonEnabled;
        });

        this.oneDriveUploadService.uploadEnabledPromise.then(uploadEnabled => {
            this.createMenuEnabled = this.createMenuEnabled || uploadEnabled;
        });

        this.initWorkloads();
    }

    public ngOnDestroy() {
        if (this.appGalleryLinkSubscription) {
            this.appGalleryLinkSubscription.unsubscribe();
        }

        if (this.previousRouteSubscription) {
            this.previousRouteSubscription.unsubscribe();
        }

        if (this.thirdPartyWorkloadsSubscription) {
            this.thirdPartyWorkloadsSubscription.unsubscribe();
        }
    }

    public async onUnpinWorkload(event: UnpinWorkloadEvent) {
        await this.workloadsService.unpinWorkload(event.item);

        // TODO: Switch to using observables instead of modifying local workloads list.
        this.workloads = this.workloads.filter(workload => !(workload.AppId === event.item.AppId && workload.CollectorId === event.item.CollectorId));

        this.changeDetector.detectChanges();
    }

    public trackByWorkloadId(_index: number, workload: Workload) {
        return workload.Id;
    }

    public isShowingReducedAppTiles() {
        return this.appTilesState === AppTilesState.Reduced;
    }

    public getDisplayedAppTilesCount(): number {
        return this.isShowingReducedAppTiles() ? Math.min(this.reducedTilesCount, this.workloads.length) : this.workloads.length;
    }

    public navigateToAppsPage(event?: Event): void {
        event?.preventDefault();
        logUserAction({
            eventName: KnownInstrumentationEvents.AppGalleryLinkClick,
            id: 'app-gallery-link',
            area: this.componentArea as Area,
            result: 'NavigateTo',
            target: Referral.AppGallery
        });
        this.officeRouteService.navigateTo({ route: OfficeRoute.AppGallery });
    }

    private initWorkloads(): void {
        if (this.isConsumer) {
            this.workloadsService.getCoreSegmentAndFrequentApps().then((workloads: Array<Readonly<Workload>>) => this.setupWorkloads(workloads));
        } else {
            this.workloadsService.getWorkloads().then((workloads: Array<Readonly<Workload>>) => this.setupWorkloads(workloads));
        }
    }

    private async setupWorkloads(workloads: Array<Readonly<Workload>>) {
        const config = this.appTilesConfigService.config;

        this.workloads = workloads.map(workload =>
            // Shell passes #0072c6 as color for all workloads without a predefined color
            ({
                ...workload
            })
        );

        if (!!this.appGalleryConfigService.config) {
            if (this.workloads.length > 0) {
                this.showAppGalleryLink = true;
            } else {
                this.thirdPartyWorkloadsSubscription = this.workloadsService.getThirdPartyWorkloads().subscribe(thirdPartyWorkloads => {
                    if (thirdPartyWorkloads.length > 0) {
                        this.showAppGalleryLink = true;
                        this.thirdPartyWorkloadsSubscription.unsubscribe();
                    }
                });
            }
        }

        this.showProvisioningAlert = this.workloads.some(workload => !workload.Url);
        let clickedOneApp = false;

        this.workloads.forEach((workload, _index) => {
            if (workload.ClickCount > 0 && this.capabilitiesService.capabilities.showFloodgate && this.capabilitiesService.capabilities.officeHomeFloodgate && !clickedOneApp) {
                clickedOneApp = true;
            }
        });

        if (clickedOneApp) {
            this.floodgateService.recordFloodgate(['ClickedOneApp']);
        }

        await this.setUpReducedTiles();

        if (!this.isConsumer) {
            if (this.showAppGalleryLink) {
                this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.ExploreAllYourAppsImpression);
                logImpression({
                    eventName: KnownInstrumentationEvents.ExploreAllYourAppsImpression,
                    area: this.componentArea as Area
                });
            }
        }

        // Adding native only workloads which are installed and used to workload list.
        this.workloadsService.getAdditionalWorkloads().then((additionalWorkloads: Array<Workload>) => {
            if (additionalWorkloads && additionalWorkloads.length > 0) {
                additionalWorkloads = additionalWorkloads.filter(workload => !workload.IsNativeClientOnlyApp || this.localStorageService.get(`AppClicked_${workload.Id}`));
                this.workloadsService.populateWorkloadFields(additionalWorkloads);
                this.workloads.splice(this.reducedTilesCount, 0, ...additionalWorkloads);
                this.reducedTilesCount += additionalWorkloads.length;
            }
        });

        this.sortTiles(config.mobileAppIds);
    }

    private async setUpReducedTiles(): Promise<void> {
        if (!this.workloads) {
            return;
        }

        return this.workloadsService.getCoreSegmentAndFrequentApps().then((appsAndWorkloads: Array<Workload>) => {
            this.appTilesState = AppTilesState.Reduced;
            this.reducedTilesCount = appsAndWorkloads.length;
            this.workloads = appsAndWorkloads;
            this.logImpressionRenderedItems(this.getDisplayedAppTilesCount());
        });
    }

    private sortTiles(orderedAppIds: Array<string>): void {
        if (!orderedAppIds) {
            return;
        }
        // since sort here is inplace, need to keep the orginal workloads order to preserve the original ordering of the sorted tiles for the apps not included in orderedAppIds
        const originalWorkloads = this.workloads.slice(0, this.workloads.length);

        this.workloads = this.workloads.sort((first, second) => {
            // Prioritize based top apps list
            for (let i = 0; i < orderedAppIds.length; i++) {
                const appId = orderedAppIds[i];
                if (first.Id === appId) {
                    return -1;
                } else if (second.Id === appId) {
                    return 1;
                }
            }

            // Finally, prioritize based on the original list
            for (let i = 0; i < originalWorkloads.length; i++) {
                if (originalWorkloads[i] === first) {
                    return -1;
                } else if (originalWorkloads[i] === second) {
                    return 1;
                }
            }

            return 0;
        });
    }

    private logImpressionRenderedItems(numRenderedItems: number) {
        let items = [];
        if (this.workloads) {
            items = this.workloads.slice(0); // Copy the items for log impression
        }

        const distributionSeparator = ';';
        const calcDistribution = (forProvisionedApp: boolean) =>
            items.reduce((combined, current, index) => {
                if (index < numRenderedItems) {
                    return (forProvisionedApp && current.Url) || (!forProvisionedApp && !current.Url)
                        ? combined + (combined.length === 0 ? '' : distributionSeparator) + getReferral(current.Id)
                        : combined;
                } else {
                    return combined;
                }
            }, '');

        const params: InstrumentationParams = {};
        params[InstrumentationParam.RenderSize] = numRenderedItems.toString();
        const distribution = calcDistribution(true);
        params[InstrumentationParam.Distribution] = distribution;
        const provisioningDistribution = calcDistribution(false);
        params[InstrumentationParam.Provisioning] = provisioningDistribution;
        const viewMode = 'Mobile';
        params[InstrumentationParam.State] = viewMode;
        params[InstrumentationParam.TotalSize] = this.workloads ? this.workloads.length.toString() : '0';
        params[InstrumentationParam.Action] = this.appTilesState;

        // Not logging any metrics because app tiles section is always displayed inside home page.
        // Home_PageView metric can be used as substitute when determing first/last occurrence
        // of app tiles section impression
        this.instrumentationService.logImpressionEvent(KnownInstrumentationEvents.AppTilesImpression, params);
        logImpression({ eventName: KnownImpressionInstrumentationEvents.AppTilesImpression, area: 'AppTiles', distribution, renderSize: numRenderedItems, totalSize: this.workloads ? this.workloads.length : 0 });
    }
}
